import { Reveal } from "./Reveal";

const Avatar = ({ name }) => (
  <span
    aria-hidden="true"
    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--brand-navy)] font-serif-brand text-lg font-semibold text-[var(--brand-gold)]"
  >
    {name.charAt(0)}
  </span>
);

const ReviewCard = ({ review, testId }) => (
  <figure
    data-testid={testId}
    className="h-full flex flex-col overflow-hidden bg-white border border-[var(--brand-gold-soft)] shadow-sm"
  >
    {review.image && (
      <div className="relative">
        <img
          src={review.image}
          alt={review.imageAlt || review.name}
          loading="lazy"
          className="h-52 sm:h-60 w-full object-cover"
          style={{ objectPosition: review.imagePosition || "50% 30%" }}
          data-testid={`${testId}-photo`}
        />
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-[var(--brand-gold)]" />
        <span
          aria-hidden="true"
          className="absolute -bottom-5 left-6 flex h-10 w-10 items-center justify-center rounded-full bg-[var(--brand-gold)] pt-2.5 font-serif-brand text-4xl leading-none text-white shadow-md"
        >
          &ldquo;
        </span>
      </div>
    )}
    <div className={`flex flex-1 flex-col p-6 sm:p-7 ${review.image ? "pt-9 sm:pt-9" : ""}`}>
      {!review.image && (
        <span className="font-serif-brand text-5xl leading-none text-[var(--brand-gold)]" aria-hidden="true">
          &ldquo;
        </span>
      )}
      <blockquote className="mt-2 flex-1 text-lg leading-relaxed text-[var(--brand-navy-deep)]">
        {review.quote}
      </blockquote>
      <figcaption className="mt-6 flex items-center gap-3 border-t border-[var(--brand-gold-soft)] pt-5">
        {!review.image && <Avatar name={review.name} />}
        <div>
          <p className="font-semibold text-[var(--brand-navy)]">{review.name}</p>
          <p className="text-sm text-[var(--brand-muted)]">
            Verified Google Review
            {review.excerpt && <span> &bull; Review excerpt</span>}
          </p>
        </div>
      </figcaption>
    </div>
  </figure>
);

const ROWS = [
  {
    painTitle: "Your symptoms get treated in pieces.",
    painBody:
      "One doctor looks at your sleep. Another runs thyroid labs. A third suggests it's stress. You're left holding a stack of test results no one has ever looked at together, while the brain fog, fatigue, and weight changes keep stacking up.",
    answer:
      "Dr. Crosson sets aside a full hour to look at the whole picture: your history, your timeline, and the results you already have.",
    review: {
      name: "Jessica Batha",
      source: "All-In Precision Wellness",
      image: "/images/reviews/jessica-batha.webp",
      imageAlt: "Jessica Batha with her family",
      imagePosition: "50% 44%",
      excerpt: false,
      quote:
        "Dr Jill was very thorough in connecting the dots between different tests ordered by different doctors. Her whole person analysis and time and care were greatly appreciated!",
    },
  },
  {
    painTitle: "You leave appointments with more questions than answers.",
    painBody:
      "Ten rushed minutes. A quick \"your labs look normal.\" The feeling that you were dismissed before you finished your first sentence. Meanwhile you're the one waking at 3 a.m., drenched and wide awake, wondering what is actually going on.",
    answer:
      "A 60-minute visit means your questions get answered in plain language, with a clear starting plan before you leave.",
    review: {
      name: "Susan Hammerstone",
      source: "All-In Precision Wellness",
      image: "/images/reviews/susan-hammerstone.webp",
      imageAlt: "Susan Hammerstone with her son",
      imagePosition: "50% 30%",
      excerpt: true,
      quote:
        "She listened to all of my concerns and explained everything in detail, going over options for bloodwork and other tests as well as where we could start with a plan for care.",
    },
  },
  {
    painTitle: "You want to be treated as a whole person, not a symptom list.",
    painBody:
      "Sleep, mood, cycles, energy, and weight don't live in separate boxes. In perimenopause they are often connected, and care that ignores those connections leaves you managing each symptom alone.",
    answer:
      "Whole-person evaluation is the foundation of every first visit, including a physical exam and an InBody body-composition assessment.",
    review: {
      name: "Grace",
      source: "Dr. Jill Crosson",
      image: "/images/reviews/grace.webp",
      imageAlt: "Grace with her family at the beach",
      imagePosition: "50% 32%",
      excerpt: true,
      quote:
        "Dr. Jill Crosson is an exceptional physician who truly listens and treats the whole person.",
    },
  },
];

export const ReviewSection = () => (
  <section id="reviews" data-testid="review-section" className="scroll-mt-20 py-16 sm:py-20 bg-white border-y border-[var(--brand-gold-soft)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <Reveal>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium text-[var(--brand-navy)] leading-tight">
          Time to listen. Care that feels personal.
        </h2>
        <p className="mt-3 text-lg text-[var(--brand-muted)]">
          Patients share their experiences with All-In Precision Wellness and Dr. Jill Crosson.
        </p>
      </Reveal>
      <div className="mt-10 space-y-10">
        {ROWS.map((row, i) => (
          <Reveal key={row.review.name} delay={i * 0.08}>
            <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-stretch">
              <div className={`flex flex-col justify-center bg-[var(--brand-cream)] border border-[var(--brand-gold-soft)] p-6 sm:p-8 ${i % 2 ? "md:order-2" : ""}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)]">
                  Sound familiar?
                </p>
                <h3 className="mt-2 font-serif-brand text-2xl font-medium text-[var(--brand-navy)]">
                  {row.painTitle}
                </h3>
                <p className="mt-3 text-base sm:text-lg leading-relaxed">{row.painBody}</p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed font-medium text-[var(--brand-navy)]">
                  {row.answer}
                </p>
              </div>
              <div className={i % 2 ? "md:order-1" : ""}>
                <ReviewCard review={row.review} testId={`review-card-${i}`} />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const InlineReview = ({ quote, name, sourceLabel, testId, light, image, sidePhoto, sidePhotoAlt, sidePhotoPosition }) => (
  <figure
    data-testid={testId}
    className={`overflow-hidden border-l-4 border-[var(--brand-gold)] ${
      light ? "bg-white" : "bg-[var(--brand-cream)]"
    } ${sidePhoto ? "sm:flex sm:items-stretch" : ""}`}
  >
    {sidePhoto && (
      <img
        src={sidePhoto}
        alt={sidePhotoAlt || name}
        loading="lazy"
        className="h-56 w-full object-cover sm:h-auto sm:w-64 sm:shrink-0"
        style={{ objectPosition: sidePhotoPosition || "50% 30%" }}
        data-testid={`${testId}-photo`}
      />
    )}
    <div className="p-6 sm:p-7">
      <blockquote className="text-lg leading-relaxed text-[var(--brand-navy-deep)]">
        &ldquo;{quote}&rdquo;
      </blockquote>
      <figcaption className="mt-4 flex items-center gap-3">
        {image ? (
          <img
            src={image}
            alt={name}
            loading="lazy"
            className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-[var(--brand-gold)]"
            data-testid={`${testId}-photo`}
          />
        ) : (
          <Avatar name={name} />
        )}
        <div>
          <p className="font-semibold text-[var(--brand-navy)]">{name}</p>
          <p className="text-sm text-[var(--brand-muted)]">{sourceLabel}</p>
        </div>
      </figcaption>
    </div>
  </figure>
);

export const HeroQuote = () => (
  <figure
    data-testid="hero-review-quote"
    className="mt-6 flex max-w-xl items-start gap-4 border-l-2 border-[var(--brand-gold)] pl-4"
  >
    <img
      src="/images/reviews/laurie-cohen.webp"
      alt="Laurie Cohen"
      loading="lazy"
      className="mt-1 h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-[var(--brand-gold)]"
      data-testid="hero-review-photo"
    />
    <div>
      <blockquote className="text-base sm:text-lg italic leading-relaxed text-[var(--brand-navy-deep)]">
        &ldquo;Dr Crosson is great. She listens, is not rushed, and explains things well. Highly
        recommend.&rdquo;
      </blockquote>
      <figcaption className="mt-1.5 text-sm text-[var(--brand-muted)]">
        &mdash; Laurie Cohen &bull; Verified Google Review
      </figcaption>
    </div>
  </figure>
);
